// GovernanceCampaign — certification campaigns + inline attestation review
// (PAM Epic 11, Task 8).
//
// OIDCPAM Gate-6 Phase 6b: migrated off MUI onto
// @devnw/cyber-iam/rbac/CertificationCampaignConsole — the campaign
// lifecycle (create -> launch -> close) + per-record keep/revoke attestation
// parity component. This CLOSES a real security gap: the pre-migration page
// fired Close and Revoke inline with NO confirm and allowed a BLANK revoke
// justification; the component requires a two-phase confirm on Close and on
// Revoke, and REQUIRES a non-empty justification for a revoke. This page
// still owns both API calls, the wire-mapping
// (../api/governanceCampaignMapping), tenant gating, and loading the
// attestation-review panel's records/attestations when a campaign is opened.
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import CertificationCampaignConsole from '@devnw/cyber-iam/rbac/CertificationCampaignConsole/CertificationCampaignConsole'
import type {
  CertificationCampaign,
  DiscoveredAccessRecord,
  Attestation,
  AttestationDecision,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import type { WireDiscoveredAccess } from '../api/governanceDiscoveryMapping'
import { toDiscoveredAccessRecord } from '../api/governanceDiscoveryMapping'
import {
  toCertificationCampaign,
  toAttestation,
  type WireCertificationCampaign,
  type WireAttestation,
} from '../api/governanceCampaignMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

export const GovernanceCampaign = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [campaigns, setCampaigns] = useState<CertificationCampaign[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [reviewCampaignId, setReviewCampaignId] = useState<string | null>(null)
  const [records, setRecords] = useState<DiscoveredAccessRecord[]>([])
  const [attestations, setAttestations] = useState<Attestation[]>([])

  const load = useCallback(async (): Promise<void> => {
    if (!tenant) return
    setLoading(true)
    setError(null)
    try {
      const data = await client.adminGovernanceListCampaigns({ tenantId: tenant })
      // The generated client's fromWire() camelCases the opaque
      // Array<Record<string, unknown>> response — see
      // ../api/governanceCampaignMapping.ts for the real (camelCase) shape.
      setCampaigns(
        ((data.campaigns ?? []) as unknown as WireCertificationCampaign[]).map(
          toCertificationCampaign
        )
      )
    } catch (e: unknown) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [tenant, client])

  useEffect(() => {
    load().then(() => {}).catch(() => {})
  }, [load])

  // The attestation-review panel's records/attestations are HOST-loaded: the
  // console only tells us which campaign was opened (onReviewCampaign), it
  // never fetches. Both calls mirror GovernanceDiscovery.tsx's list-discovery
  // wire-mapping and the campaign attestation wire-mapping respectively.
  const loadReview = useCallback(
    async (campaignId: string): Promise<void> => {
      if (!tenant) return
      setError(null)
      try {
        const [discoveryData, attestationData] = await Promise.all([
          client.adminGovernanceListDiscovery({ tenantId: tenant }),
          client.adminGovernanceListAttestations({
            tenantId: tenant,
            campaignId,
          }),
        ])
        setRecords(
          ((discoveryData.items ?? []) as unknown as WireDiscoveredAccess[]).map(
            toDiscoveredAccessRecord
          )
        )
        setAttestations(
          (
            (attestationData.attestations ?? []) as unknown as WireAttestation[]
          ).map(toAttestation)
        )
      } catch (e: unknown) {
        setError(errMessage(e))
      }
    },
    [tenant, client]
  )

  const handleReviewCampaign = (id: string | null): void => {
    setReviewCampaignId(id)
    if (id != null) {
      loadReview(id).then(() => {}).catch(() => {})
    } else {
      setRecords([])
      setAttestations([])
    }
  }

  const handleRefresh = (): void => {
    load().then(() => {}).catch(() => {})
    if (reviewCampaignId != null) {
      loadReview(reviewCampaignId).then(() => {}).catch(() => {})
    }
  }

  const handleCreateCampaign = async (input: {
    name: string
    dueAt: string
  }): Promise<void> => {
    if (!tenant) return
    await client.adminGovernanceCreateCampaign({
      tenantId: tenant,
      name: input.name,
      dueAt: new Date(input.dueAt).toISOString(),
      scope: { all_subjects: true },
    })
  }

  const handleLaunchCampaign = async (id: string): Promise<void> => {
    if (!tenant) return
    await client.adminGovernanceLaunchCampaign({ tenantId: tenant, campaignId: id })
  }

  const handleCloseCampaign = async (id: string): Promise<void> => {
    if (!tenant) return
    await client.adminGovernanceCloseCampaign({ tenantId: tenant, campaignId: id })
  }

  const handleSubmitAttestation = async (input: {
    campaignId: string
    discoveryId: string
    decision: AttestationDecision
    justification?: string
  }): Promise<void> => {
    if (!tenant) return
    await client.adminGovernanceSubmitAttestation({
      tenantId: tenant,
      campaignId: input.campaignId,
      discoveryId: input.discoveryId,
      decision: input.decision,
      justification: input.justification ?? '',
    })
  }

  if (!tenant) {
    return (
      <PageContainer>
        <MutedText>Select a tenant to view certification campaigns.</MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <CertificationCampaignConsole
        testID="governance-campaign"
        title="Certification campaigns"
        description="Periodic access certification campaigns. Reviewers attest (keep) or revoke each entitlement. Un-attested items past due are auto-flagged."
        campaigns={campaigns}
        onCreateCampaign={handleCreateCampaign}
        onLaunchCampaign={handleLaunchCampaign}
        onCloseCampaign={handleCloseCampaign}
        reviewCampaignId={reviewCampaignId ?? undefined}
        records={records}
        attestations={attestations}
        onReviewCampaign={handleReviewCampaign}
        onSubmitAttestation={handleSubmitAttestation}
        onViewResults={handleReviewCampaign}
        onRefresh={handleRefresh}
        loading={loading}
        error={error ?? undefined}
      />
    </PageContainer>
  )
}
